import {Injectable} from '@angular/core';
import {ProjectElementStore} from '../../utilities/stores/project-element/project-element-store';
import {ProjectSubmoduleStore} from '../../utilities/stores/project-submodule/project-submodule-store';
import {ProjectModuleStore} from '../../utilities/stores/project-module/project-module-store';
import {ProjectElement} from '../../resources/element/project-element.interface';
import {ProjectSubmodule} from '../../resources/submodule/project-submodule.interface';
import {ProjectModule} from '../../resources/module/project-module.interface';

import {combineLatest} from 'rxjs';

/**
 * @depricated But still in use by previous datastore. Do not remove until after full migration.
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectChapterService {

  /** Module chapter. */
  CHAPTER_MODULE_INITIAL = 0;
  moduleChapter = this.CHAPTER_MODULE_INITIAL;

  /** Submodule chapter. */
  CHAPTER_SUBMODULE_INITIAL = 0;
  submoduleChapter = this.CHAPTER_SUBMODULE_INITIAL;

  /** Element chapter. */
  CHAPTER_ELEMENT_INITIAL = 1;
  chapterElement = this.CHAPTER_ELEMENT_INITIAL;

  projectElements: ProjectElement[];
  projectSubmodules: ProjectSubmodule[];
  projectModules: ProjectModule[];

  constructor(private projectElementStore: ProjectElementStore,
              private projectSubmoduleStore: ProjectSubmoduleStore,
              private projectModuleStore: ProjectModuleStore) {

    // Listen to all obervables
    combineLatest([this.projectElementStore.state$, this.projectSubmoduleStore.state$, this.projectModuleStore.state$])
      .subscribe(([projectElements, projectSubmodules, projectModules]) => {

        // Only start updating if we have the complete info
      if (projectElements.length && projectSubmodules.length && projectModules.length) {

        this.projectElements = projectElements;
        this.projectSubmodules = projectSubmodules;
        this.projectModules = projectModules;

        // Filter out elements of wrong project level, because they do not count for chapter numbers
        this.projectElements = this.projectElements.filter(pe => [1, 2].includes(pe.projectLevel));

        // Now get a list of the moduleId's used in those elements
        const chapteredModuleIds = Array.from(new Set(this.projectElements.map(pe => pe.moduleId)));
        this.projectModules = this.projectModules.filter(m => chapteredModuleIds.includes(m._id));

        this.addChaptersToProject();
      }
    });
  }

  /**
   * Update the complete project with chapter numbers.
   *
   * This function should run on every update to the project. See @todo.
   *
   * @todo Once there is a Project observable, this should be added to the project update pipeline.
   *
   * @param modules The project's modules.
   * @returns The project's modules with chapter numbers.
   */
  addChaptersToProject() {

    this.resetAllChapters();

    // sort
    this.projectModules.sort((a, b) => (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0));

    // Modules.
    for (const module of this.projectModules) {
      module.chapter = '';

      const submodules = this.projectSubmodules.filter(o => o.active && o.moduleId === module._id);
      const elementsForModule = this.projectElements.filter(o => o.active && o.moduleId === module._id);

      // Only count chapters if this module has submodulesand elements in it's submodules
      if (submodules.length && elementsForModule.length) {

        // Sort
        submodules.sort((a, b) => (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0));

        this.incrementModuleChapter();
        module.chapter = this.getModuleChapter();

        // Submodules.
        for (const submodule of submodules) {
          submodule.chapter = '';
          const elements = elementsForModule.filter(el => el.submoduleId === submodule._id);

          // Only add chapter information for submodules that have elements
          if (elements.length) {
            if (!(submodule.order === 0 && (submodule.name === 'Algemeen' || module.name === submodule.name))) {
              this.incrementSubmoduleChapter();
              submodule.chapter = this.getSubmoduleChapter();
            }

            // Elements.
            let elementIndex = 0;
            for (const element of elements) {
              elementIndex++;
              // Reset to base
              element.chapter = '';
              element.submoduleChapter = '';
              element.moduleChapter = this.getModuleChapter();

              if (submodule.chapter === '') {
                if (!(elementIndex === 1 && (element.name === 'Algemeen' || element.name === submodule.name))) {
                  this.incrementSubmoduleChapter();
                  element.chapter = this.getSubmoduleChapter();
                }
              } else {
                if (elementIndex === 1 && (element.name === 'Algemeen' || element.name === submodule.name)) {
                  element.submoduleChapter = this.getSubmoduleChapter();
                } else {
                  element.submoduleChapter = this.getSubmoduleChapter();
                  element.chapter = this.getElementChapter();

                  this.incrementElementChapter();
                }
              }
            }
          }

          if (submodule.name === 'hallo') {
            console.log('submodule', submodule);
          }
          // For each new submodule, reset element chapter to initial state.
          this.resetElementChapter();
        }
      }
      // For each new module, reset element chapter to initial state.
      this.resetSubmoduleChapter();
    }
  }

  /**
   * Get module chapter number.
   *
   * @returns Module chapter number.
   */
  getModuleChapter() {
    return `${this.moduleChapter}`;
  }

  /**
   * Get submodule chapter number.
   *
   * @returns Submodule chapter number.
   */
  getSubmoduleChapter() {
    return `${this.moduleChapter}.${this.submoduleChapter}`;
  }

  /**
   * Get element chapter number.
   *
   * @returns Element chapter number.
   */
  getElementChapter() {
    return `${this.moduleChapter}.${this.submoduleChapter}.${this.chapterElement}`;
  }

  /**
   * Increment module chapter number by 1.
   */
  incrementModuleChapter() {
    this.moduleChapter++;
  }

  /**
   * Increment submodule chapter number by 1.
   */
  incrementSubmoduleChapter() {
    this.submoduleChapter++;
  }

  /**
   * Increment element chapter number by 1.
   */
  incrementElementChapter() {
    this.chapterElement++;
  }

  /**
   * Reset all chapters to initial state.
   */
  resetAllChapters() {
    this.resetModuleChapter();
    this.resetSubmoduleChapter();
    this.resetElementChapter();
  }

  /**
   * Reset module chapter number.
   */
  resetModuleChapter() {
    this.moduleChapter = this.CHAPTER_MODULE_INITIAL;
  }

  /**
   * Reset submodule chapter number.
   */
  resetSubmoduleChapter() {
    this.submoduleChapter = this.CHAPTER_SUBMODULE_INITIAL;
  }

  /**
   * Reset element chapter number.
   */
  resetElementChapter() {
    this.chapterElement = this.CHAPTER_ELEMENT_INITIAL;
  }
}
